import React, { useEffect, useState } from 'react';
import { Truck, ExternalLink, X } from 'lucide-react';
import { portalApi, sessionHeaders, portalErrorMessage } from './portalApi';
import { STATUS_COLOURS, StatusPill, BackorderPill, usePortalGet } from './PortalOrdersPage';
import { STACK_GAP } from '../DashboardChart';

// The portal's Home page: a welcome that says what the portal is, then the
// Customer's own figures (backend portalOrderService.dashboard) — orders at
// each status, the latest shipment, spend per month with its total and
// order count, and their most bought products.

const CARD_STYLE = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 20, boxSizing: 'border-box', minWidth: 0 };
const MUTED = '#5a6b85';
const INK = '#1f2d3d';
const ACCENT = '#1976d2';
const CARD_TITLE = { margin: '0 0 12px', fontSize: 15, fontWeight: 600 };
const LINK_BUTTON = { background: 'none', border: 'none', padding: 0, color: ACCENT, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' };

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;


const CHART_HEIGHT = 150;
const PENDING_COLOUR = '#9cc3ec';
const MONTH_ORDERS_ERROR = "Could not load that month's orders";

function LegendKey({ colour, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 10, height: 10, borderRadius: 2, background: colour }} />
      {label}
    </span>
  );
}

// Spend per month for the last 12 months, one bar each, stacked Shipped
// (solid) under Pending (light). Hovering a month (or focusing it) shows
// both and the order count; clicking a month with orders picks it, and
// MonthOrders lists them under the chart.
function MonthlySpendChart({ months, selectedKey, onSelect, incGst }) {
  const [active, setActive] = useState(null);
  const max = Math.max(...months.map((m) => m.total), 0);
  return (
    <div>
      <div style={{ position: 'relative', height: CHART_HEIGHT, display: 'flex', alignItems: 'flex-end', gap: 2, borderBottom: '1px solid #d7dbe3' }}>
        {months.map((m, i) => {
          const height = max > 0 ? Math.max((m.total / max) * (CHART_HEIGHT - 20), m.total > 0 ? 3 : 0) : 0;
          const pendingHeight = m.total > 0 ? height * (m.pending / m.total) : 0;
          const gap = m.shipped > 0 && m.pending > 0 ? STACK_GAP : 0;
          const clickable = m.orders > 0;
          const selected = m.key === selectedKey;
          const pick = () => { if (clickable) onSelect(selected ? null : m.key); };
          return (
            <div
              key={m.key}
              tabIndex={0}
              role={clickable ? 'button' : undefined}
              aria-pressed={clickable ? selected : undefined}
              aria-label={`${m.label} ${m.year}: ${m.total_label}, ${plural(m.orders, 'order')}`}
              title={clickable ? `Show your orders from ${m.label} ${m.year}` : undefined}
              onClick={pick}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(i)}
              onBlur={() => setActive(null)}
              style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', position: 'relative', outline: 'none', background: selected ? '#e8f0fb' : active === i ? '#f5f8fc' : 'transparent', borderRadius: '4px 4px 0 0', cursor: clickable ? 'pointer' : 'default' }}
            >
              {m.bar_label && <div style={{ fontSize: 11, color: MUTED, marginBottom: 3, whiteSpace: 'nowrap' }}>{m.bar_label}</div>}
              <div style={{ width: '62%', maxWidth: 34, height: height + gap, display: 'flex', flexDirection: 'column', gap, borderRadius: '4px 4px 0 0', overflow: 'hidden', opacity: active === null || active === i ? 1 : 0.55 }}>
                {m.pending > 0 && <div style={{ height: pendingHeight, flexShrink: 0, background: PENDING_COLOUR }} />}
                {m.shipped > 0 && <div style={{ flex: 1, background: ACCENT }} />}
              </div>
              {active === i && (
                <div style={{
                  position: 'absolute', bottom: '100%', marginBottom: 6, zIndex: 2,
                  ...(i > months.length / 2 ? { right: 0 } : { left: 0 }),
                  background: INK, color: '#fff', fontSize: 12, padding: '6px 9px', borderRadius: 6, whiteSpace: 'nowrap', pointerEvents: 'none',
                }}
                >
                  <div style={{ fontWeight: 600 }}>{m.label} {m.year}</div>
                  {m.pending > 0 && <div>Pending: {m.pending_label}</div>}
                  {m.shipped > 0 && <div>Shipped: {m.shipped_label}</div>}
                  <div>{m.total_label} · {plural(m.orders, 'order')}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 2, marginTop: 6 }}>
        {months.map((m) => (
          <div key={m.key} style={{ flex: 1, textAlign: 'center', fontSize: 11, color: MUTED, overflow: 'hidden', whiteSpace: 'nowrap' }}>{m.label}</div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12, fontSize: 12, color: INK }}>
        <LegendKey colour={ACCENT} label="Shipped" />
        <LegendKey colour={PENDING_COLOUR} label="Pending" />
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: MUTED }}>
        By order date{incGst ? ', inc GST' : ''}. Excludes cancelled items, withdrawn orders and shipping not yet quoted.
      </div>
    </div>
  );
}

// The orders behind one month's bar, newest first; a row opens its order.
function MonthOrders({ portalName, month, onOpenOrder, onClose, onSessionEnded }) {
  const { data: orders, error } = usePortalGet(portalName, '/orders', { month: month.key }, onSessionEnded, MONTH_ORDERS_ERROR);
  const th = { padding: '6px 10px 6px 0', fontSize: 12, fontWeight: 600, color: MUTED, textAlign: 'left', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px 8px 0', fontSize: 14, verticalAlign: 'top' };
  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #eef1f5' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Orders in {month.label} {month.year}</h3>
        <button type="button" onClick={onClose} title="Hide this month's orders" aria-label="Hide this month's orders" style={{ ...LINK_BUTTON, color: MUTED, display: 'inline-flex' }}><X size={16} /></button>
      </div>
      {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
      {!error && orders === null && <div style={{ fontSize: 13, color: MUTED }}>Loading...</div>}
      {orders && orders.length === 0 && <div style={{ fontSize: 13, color: MUTED }}>No orders that month.</div>}
      {orders && orders.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #d7dbe3' }}>
                <th style={th}>Order</th>
                <th style={th}>Date</th>
                <th style={th}>PO reference</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right', paddingRight: 0 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => onOpenOrder(o.id)}
                  title={`Open ${o.display_number}`}
                  style={{ borderBottom: '1px solid #eef1f5', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f8fc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ ...td, fontWeight: 500, color: ACCENT, whiteSpace: 'nowrap' }}>{o.display_number}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{o.order_date}</td>
                  <td style={td}>{o.po_reference || '—'}</td>
                  <td style={td}>
                    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                      <StatusPill status={o.status} label={o.status_label} />
                      {o.backordered && <BackorderPill />}
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: 'right', paddingRight: 0, whiteSpace: 'nowrap' }}>
                    {o.total}
                    {o.total_excludes_shipping && <div style={{ fontSize: 11, color: '#888' }}>excl. shipping</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Status tiles as chevrons, so the row reads as the order's flow: each
// points into the next, which notches to take it. The first keeps a flat
// left edge and the last a flat right one; the overlap leaves a 4px gap.
const CHEVRON_DEPTH = 14;
function chevronStyle(i, count) {
  const d = `${CHEVRON_DEPTH}px`;
  const first = i === 0;
  const last = i === count - 1;
  const points = [
    '0 0',
    last ? '100% 0' : `calc(100% - ${d}) 0`,
    ...(last ? [] : ['100% 50%']),
    last ? '100% 100%' : `calc(100% - ${d}) 100%`,
    '0 100%',
    ...(first ? [] : [`${d} 50%`]),
  ];
  return {
    clipPath: `polygon(${points.join(', ')})`,
    marginLeft: first ? 0 : 4 - CHEVRON_DEPTH,
    padding: `12px ${last ? 14 : 14 + CHEVRON_DEPTH}px 12px ${first ? 14 : 14 + CHEVRON_DEPTH}px`,
  };
}

// How many orders sit at each status, Submitted first, coloured like the
// status pills on the Orders page. Shipped counts this year's orders.
function StatusSummary({ data, branding, onOpenOrders }) {
  return (
    <div style={CARD_STYLE}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ ...CARD_TITLE, margin: 0 }}>Orders</h2>
        <button type="button" onClick={onOpenOrders} title="Show your orders" style={LINK_BUTTON}>All orders ›</button>
      </div>
      <div style={{ display: 'flex' }}>
        {data.status_summary.map((s, i) => {
          const colours = STATUS_COLOURS[s.status] || STATUS_COLOURS.accepted;
          return (
            <button
              key={s.status}
              type="button"
              onClick={onOpenOrders}
              title="Show your orders"
              style={{ ...chevronStyle(i, data.status_summary.length), flex: '1 1 0', minWidth: 0, border: 'none', borderRadius: 10, background: colours.background, color: colours.color, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{s.count}</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{s.status === 'shipped' ? `Shipped in ${data.year}` : s.label}</div>
            </button>
          );
        })}
      </div>
      {data.with_backorders > 0 && (
        <div style={{ fontSize: 13, color: MUTED, marginTop: 10 }}>
          <span style={{ color: '#b91c1c', fontWeight: 600 }}>●</span> {plural(data.with_backorders, 'order')} with backorders
        </div>
      )}
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #eef1f5' }}>
        <MissingOrderNote branding={branding} />
      </div>
    </div>
  );
}

// Only orders entered in Optim show here, so an order sent by email that
// hasn't been entered yet won't appear — this says who to ask.
function MissingOrderNote({ branding }) {
  const email = branding?.contact_email;
  const phone = branding?.contact_phone;
  const who = branding?.company_name || 'us';
  return (
    <div style={{ fontSize: 13, color: MUTED }}>
      Don&apos;t see all of your orders? Contact {who}
      {email && <> at <a href={`mailto:${email}`} style={{ color: ACCENT }}>{email}</a></>}
      {phone && <>{email ? ' or ' : ' on '}<a href={`tel:${phone.replace(/\s+/g, '')}`} style={{ color: ACCENT }}>{phone}</a></>}
      .
    </div>
  );
}

// The Shipments card, laid out like Orders: title with All shipments
// top right, then the latest shipment (carrier, ship date, order and
// tracking number, which links to the carrier's tracking page when it has one).
function LatestShipment({ shipment, onOpenOrder, onOpenAll }) {
  return (
    <div style={CARD_STYLE}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ ...CARD_TITLE, margin: 0 }}>Shipments</h2>
        <button type="button" onClick={onOpenAll} title="Show all your shipments" style={LINK_BUTTON}>All shipments ›</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', width: 36, height: 36, borderRadius: 18, background: '#e6f4ea', color: '#2e7d32', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Truck size={18} />
        </span>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ fontSize: 14 }}>
            <span style={{ color: MUTED }}>Latest</span>
            {' · '}
            <strong>{shipment.carrier || 'Shipped'}</strong>
            {' · '}
            {shipment.shipped_date}
          </div>
          <div style={{ fontSize: 13, color: MUTED, wordBreak: 'break-all' }}>
            <button type="button" onClick={() => onOpenOrder(shipment.order_id)} title={`Open ${shipment.display_number}`} style={LINK_BUTTON}>
              {shipment.display_number}
            </button>
            {shipment.tracking_number && (
              <>
                {' · Tracking '}
                {shipment.tracking_url ? (
                  <a
                    href={shipment.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Track this shipment on ${shipment.carrier}'s website`}
                    style={{ color: ACCENT, textDecoration: 'none' }}
                  >
                    {shipment.tracking_number} <ExternalLink size={12} style={{ verticalAlign: -1 }} />
                  </a>
                ) : shipment.tracking_number}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PortalHome({ portalName, me, branding, onNavigate, onSessionEnded }) {
  const companyName = branding?.company_name;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    portalApi.get(`/api/portal-auth/portals/${encodeURIComponent(portalName)}/dashboard`, { headers: sessionHeaders(portalName) })
      .then((res) => { if (!cancelled) setData(res.data.data); })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) onSessionEnded();
        else setError(portalErrorMessage(err, 'Could not load your summary'));
      });
    return () => { cancelled = true; };
  }, [portalName, onSessionEnded]);

  // Only the chart follows the chosen year; the rest of the page stays on
  // this year.
  const changeChartYear = async (year) => {
    setChartLoading(true);
    setChartError('');
    setSelectedMonth(null);
    try {
      const res = await portalApi.get(`/api/portal-auth/portals/${encodeURIComponent(portalName)}/dashboard`, {
        params: year ? { chart_year: year } : {},
        headers: sessionHeaders(portalName),
      });
      const next = res.data.data;
      setData((prev) => ({ ...prev, chart_year: next.chart_year, chart_years: next.chart_years, chart_summary: next.chart_summary, monthly_spend: next.monthly_spend }));
    } catch (err) {
      if (err.response?.status === 401) onSessionEnded();
      else setChartError(portalErrorMessage(err, 'Could not load that year'));
    }
    setChartLoading(false);
  };

  const company = companyName || 'our';
  const openOrder = (id) => onNavigate(`orders/${id}`);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...CARD_STYLE, padding: 24, background: 'linear-gradient(120deg, #1565c0 0%, #1e88e5 60%, #42a5f5 100%)', color: '#fff' }}>
        <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4 }}>{companyName ? `${companyName} Customer Portal` : 'Customer Portal'}</div>
        <h1 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 700 }}>Welcome back, {me.customer_name}</h1>
        <div style={{ fontSize: 14, opacity: 0.92 }}>
          Your {company} customer portal: download your pricelists, track your orders and keep your account details up to date, all in one place.
        </div>
        {data?.customer_since && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 10 }}>Ordering with us since {data.customer_since}</div>}
      </div>

      {error && <div style={{ ...CARD_STYLE, color: 'red', fontSize: 13 }}>{error}</div>}
      {!error && !data && <div style={{ ...CARD_STYLE, color: MUTED, fontSize: 14 }}>Loading your summary...</div>}

      {data && data.has_orders && (
        <>
          <StatusSummary data={data} branding={branding} onOpenOrders={() => onNavigate('orders')} />

          {data.latest_shipment && <LatestShipment shipment={data.latest_shipment} onOpenOrder={openOrder} onOpenAll={() => onNavigate('shipments')} />}

          <div>
            <div style={CARD_STYLE}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <h2 style={{ ...CARD_TITLE, margin: 0 }}>Spend by month: {data.chart_year || 'last 12 months'}</h2>
                <select
                  value={data.chart_year || ''}
                  onChange={(e) => changeChartYear(e.target.value)}
                  disabled={chartLoading}
                  aria-label="Show spend for"
                  title="Choose which months the chart shows"
                  style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 13, fontFamily: 'inherit', background: '#fff', color: INK }}
                >
                  <option value="">Last 12 months</option>
                  {data.chart_years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div style={{ fontSize: 13, color: MUTED, margin: '-4px 0 14px' }}>
                <strong style={{ color: INK }}>{data.chart_summary.label}{data.gst_suffix}: {data.chart_summary.spent}</strong>
                {' · '}{plural(data.chart_summary.orders, 'order')}
              </div>
              {chartError && <div style={{ color: 'red', fontSize: 13, marginBottom: 8 }}>{chartError}</div>}
              <div style={{ opacity: chartLoading ? 0.5 : 1 }}>
                <MonthlySpendChart months={data.monthly_spend} selectedKey={selectedMonth} onSelect={setSelectedMonth} incGst={!!data.gst_suffix} />
              </div>
              {selectedMonth && data.monthly_spend.some((m) => m.key === selectedMonth) && (
                <MonthOrders
                  portalName={portalName}
                  month={data.monthly_spend.find((m) => m.key === selectedMonth)}
                  onOpenOrder={openOrder}
                  onClose={() => setSelectedMonth(null)}
                  onSessionEnded={onSessionEnded}
                />
              )}
            </div>
          </div>

          {data.top_products.length > 0 && (
            <div style={CARD_STYLE}>
              <h2 style={CARD_TITLE}>Top products in {data.year}</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #d7dbe3', fontSize: 12, color: MUTED, textAlign: 'left' }}>
                      <th style={{ padding: '6px 10px 6px 0', fontWeight: 600 }}>Product code</th>
                      <th style={{ padding: '6px 10px 6px 0', fontWeight: 600 }}>Description</th>
                      <th style={{ padding: '6px 10px 6px 0', fontWeight: 600, textAlign: 'right' }}>Qty</th>
                      <th style={{ padding: '6px 0', fontWeight: 600, textAlign: 'right' }}>{data.top_products_spend_label}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_products.map((p) => (
                      <tr key={p.code} style={{ borderBottom: '1px solid #eef1f5', fontSize: 14 }}>
                        <td style={{ padding: '8px 10px 8px 0', whiteSpace: 'nowrap' }}>{p.code}</td>
                        <td style={{ padding: '8px 10px 8px 0' }}>{p.description}</td>
                        <td style={{ padding: '8px 10px 8px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>{p.qty}{p.unit ? ` ${p.unit}` : ''}</td>
                        <td style={{ padding: '8px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>{p.spend}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {data && !data.has_orders && (
        <div style={{ ...CARD_STYLE, fontSize: 14, color: MUTED }}>
          You have no orders yet. Once you place one, you&apos;ll see its progress and your spending here.
          <div style={{ marginTop: 8 }}><MissingOrderNote branding={branding} /></div>
        </div>
      )}
    </div>
  );
}

export default PortalHome;
